import React, { useRef, useState } from "react";
import {
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  FormHelperText,
  Checkbox,
  IconButton,
  Slider,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogActions,
} from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";

export const Widgets = [
  {
    id: "text-input",
    name: "Text Input",
    icon: "🔤",
    component: (props: any) => {
      const {
        label,
        required,
        hintText,
        defaultText,
        inputType,
        maxCharacters,
        editable,
        regexValidation,
        description,
        value,
        onChange,
      } = props;

      // Validation function
      const validate = (val: string) => {
        if (required && val.trim() === "") {
          return "This field is required";
        }
        if (maxCharacters && val.length > maxCharacters) {
          return `Maximum length is ${maxCharacters}`;
        }
        if (
          regexValidation &&
          val.trim() !== "" &&
          !new RegExp(regexValidation).test(val)
        ) {
          return "Regex validation failed";
        }
        return null;
      };

      const error = validate(value || defaultText || "");
      return (
        <TextField
          fullWidth
          size="small"
          type={inputType["selected"] || "text"}
          label={label}
          value={value || ""}
          required={required}
          placeholder={hintText}
          inputProps={{ maxLength: maxCharacters, readOnly: !editable }}
          onChange={(e) => onChange(e.target.value)}
          error={Boolean(error)}
          helperText={
            error || (description ? "This is a description field" : "")
          }
        />
      );
    },
    config: {
      label: "TEXT BOX",
      hintText: "write here",
      required: false,
      defaultText: "",
      inputType: {
        type: "options",
        values: ["text", "email", "password", "number", "tel", "url"],
        selected: "",
      },
      maxCharacters: 0,
      editable: true,
      description: true,
      regexValidation: "",
      value: "",
    },
  },
  {
    id: "checkbox",
    name: "Checkbox",
    icon: "✅",
    component: (props: any) => {
      const { label, checked, list = [], required, onChange } = props;

      // Ensure checked is always an array when dealing with multiple checkboxes
      const checkedValues = Array.isArray(checked) ? checked : [];

      return (
        <div>
          {list.length > 0 ? (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "4px" }}
            >
              <div
                style={{
                  marginBottom: "6px",
                  fontSize: "16px",
                  fontWeight: 500,
                }}
              >
                <span>{label}</span>
                {required && <span style={{ color: "red" }}>*</span>}
              </div>
              {list.map((item: string | number, index: number) => (
                <FormControlLabel
                  key={index}
                  control={
                    <Checkbox
                      checked={checkedValues.includes(item)}
                      onChange={(e) => {
                        const newChecked = [...checkedValues];
                        if (e.target.checked) newChecked.push(item);
                        else newChecked.splice(newChecked.indexOf(item), 1);
                        onChange(newChecked);
                      }}
                      color="primary"
                    />
                  }
                  label={String(item)}
                />
              ))}
            </div>
          ) : (
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(checked)}
                  onChange={(e) => onChange(e.target.checked)}
                  color="primary"
                />
              }
              label={label}
            />
          )}
          {required && list.length === 0 && !checked && (
            <FormHelperText error>Checkbox is required</FormHelperText>
          )}
        </div>
      );
    },
    config: {
      label: "Check this box",
      checked: [], // Ensure default value is an array
      list: ["Option 1", "Option 2", "Option 3"], // Dynamic list support
      required: false,
    },
  },
  {
    id: "radio-group",
    name: "Radio",
    icon: "🔘",
    component: (props: any) => {
      const { value, onChange, config } = props;
      const options = config.list || [];

      return (
        <div>
          <RadioGroup
            row
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
          >
            {options.length > 0
              ? options.map((option: string, index: number) => (
                  <FormControlLabel
                    key={index}
                    value={option}
                    control={<Radio color="primary" />}
                    label={option}
                  />
                ))
              : "No options available"}
          </RadioGroup>
          {config.required && !value && (
            <FormHelperText error>Please select an option</FormHelperText>
          )}
        </div>
      );
    },
    config: {
      label: "Choose an option",
      value: "",
      list: ["Yes", "No", "Maybe"], // Dynamic options
      required: true,
    },
  },
  {
    id: "ranged-date",
    name: "Ranged Date",
    icon: "📅",
    component: (props: any) => {
      const { value = {}, onChange, config } = props;
      const { startDate = "", endDate = "" } = value;
  
      const handleChange = (key: string, val: string) => {
        const updated = { ...value, [key]: val };
        onChange(updated);
      };
  
      const getDateDiff = () => {
        if (startDate && endDate) {
          const start = new Date(startDate);
          const end = new Date(endDate);
          const diffTime = end.getTime() - start.getTime();
          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
          return `${diffDays} day(s)`;
        }
        return "";
      };
  
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <div style={{ display: "flex", gap: "12px" }}>
          <TextField
            label="Start Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={startDate}
            onChange={(e) => handleChange("startDate", e.target.value)}
          />
          <TextField
            label="End Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={endDate}
            onChange={(e) => handleChange("endDate", e.target.value)}
          />
          </div>
          {config.difference && (
            <TextField
              label="Difference"
              value={getDateDiff()}
              InputProps={{ readOnly: true }}
            />
          )}
        </div>
      );
    },
    config: {
      label: "Date Range",
      required: true,
      difference: true,
      startDate: "",
      endDate: ""
    }
  },  
  {
    id: "textarea",
    name: "Textarea",
    icon: "📝",
    component: (props: any) => (
      <TextField
        fullWidth
        multiline
        minRows={3}
        label={props.label}
        placeholder={props.placeholder}
        value={props.value || ""}
        onChange={(e) => props.onChange(e.target.value)}
      />
    ),
    config: {
      label: "Enter description",
      placeholder: "Write here...",
      value: "",
    },
  },
  {
    id: "date-picker",
    name: "Date Picker",
    icon: "📅",
    component: (props: any) => (
      <TextField
        fullWidth
        type="date"
        placeholder={props.hint}
        required={props.required}
        value={props.value || ""}
        onChange={(e) => props.onChange(e.target.value)}
      />
    ),
    config: {
      label: "date picker",
      defaultDate: "",
      hint: "",
      required: true,
    },
  },
  {
    id: "slider",
    name: "Slider",
    icon: "🎚️",
    component: (props: any) => {
      return (
        <Slider
          value={props.value || props.min}
          min={Number(props.min)}
          max={Number(props.max)}
          step={Number(props.step)}
          onChange={(_, newValue) => props.onChange(newValue)}
          valueLabelDisplay="auto"
          onMouseOutCapture={() => props.setIsDragDisabledState?.(false)} // Restore dragging
          onMouseOver={() => props.setIsDragDisabledState?.(true)} // Prevent dragging
        />
      );
    },
    config: {
      label: "Adjust Value",
      value: 30,
      min: 0,
      max: 100,
      step: 5,
    },
  },
  {
    id: "dropdown",
    name: "Dropdown",
    icon: "⬇️",
    component: (props: any) => {
      const {
        list = [],
        hintText,
        required,
        value,
        defaultOption,
        onChange,
      } = props;

      return (
        <TextField
          select
          fullWidth
          size="small"
          label={hintText || "Select an option"}
          value={value || defaultOption || ""}
          onChange={(e) => onChange(e.target.value)}
          error={required && !value}
          helperText={required && !value ? "This field is required" : ""}
          SelectProps={{ native: true }}
        >
          <option value="" disabled>
            {defaultOption || "Choose..."}
          </option>
          {list.map((option: string, index: number) => (
            <option key={index} value={option}>
              {option}
            </option>
          ))}
        </TextField>
      );
    },
    config: {
      label: "Dropdown",
      hintText: "Select an option",
      required: false,
      list: ["Option 1", "Option 2", "Option 3"], // Example options
      defaultOption: "Option 1",
      value: "",
    },
  },
  {
    id: "multiselect-dropdown",
    name: "Multi-Select Dropdown",
    icon: "🔽",
    component: (props: any) => {
      const {
        list = [],
        required,
        hint,
        maxOptions,
        value = [],
        onChange,
      } = props;

      const handleChange = (event: any) => {
        const selectedValues = event.target.value;
        if (
          Array.isArray(selectedValues) &&
          selectedValues.length <= maxOptions
        ) {
          onChange(selectedValues);
        }
      };

      const error =
        required && (!Array.isArray(value) || value.length === 0)
          ? "At least one option must be selected"
          : "";

      return (
        <FormControl fullWidth size="small" error={Boolean(error)}>
          <InputLabel>{hint || "Select options"}</InputLabel>
          <Select
            multiple
            value={Array.isArray(value) ? value : []} // Ensure value is always an array
            onChange={handleChange}
            renderValue={(selected) => selected.join(", ")}
          >
            {list.map((option: string, index: number) => (
              <MenuItem key={index} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
          {error && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
      );
    },
    config: {
      label: "Multi-Select Dropdown",
      hint: "Select multiple options",
      required: true,
      list: ["Option 1", "Option 2", "Option 3", "Option 4", "Option 5"], // Example list
      maxOptions: 4,
      value: [], // Ensure it's an array initially
    },
  },
  {
    id: "camera",
    name: "Camera",
    icon: "📷",
    component: (props: any) => <CameraWidget {...props} />,
    config: {
      label: "Image",
      required: true,
      gallery: true, // Allow image selection from gallery if true
      camera: true, // Allow direct camera capture if true
      value: "", // Holds the image URL for preview
    },
  },
  {
    id: "location",
    name: "Location",
    icon: "📍",
    component: (props: any) => {
      const { display, value = {}, onChange } = props;

      return display ? (
        <Box>
          <Typography variant="caption">Select Location</Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter location"
            value={value.location || ""}
            onChange={(e) => onChange({ ...value, location: e.target.value })}
          />
          <Box sx={{ display: "flex", gap: 1, marginTop: 1 }}>
            <TextField
              size="small"
              label="Latitude"
              type="number"
              value={value?.lat || ""}
              onChange={(e) => onChange({ ...value, lat: e.target.value })}
            />
            <TextField
              size="small"
              label="Longitude"
              type="number"
              value={value?.lng || ""}
              onChange={(e) => onChange({ ...value, lng: e.target.value })}
            />
          </Box>
        </Box>
      ) : null;
    },
    config: {
      label: "Location",
      display: true,
      value: {
        location: "",
        lat: "",
        lng: "",
      },
    },
  },
];

const CameraWidget: React.FC<any> = ({ config, onChange }) => {
  const { label, required, gallery, camera } = config;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [blobData, setBlobData] = useState<Blob | null>(null);
  const [open, setOpen] = useState(false);
  const [showCamera, setShowCamera] = useState(false);

  const handleCapture = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const blob = new Blob([file], { type: file.type });
      setBlobData(blob);
      setPreview(URL.createObjectURL(blob));
      onChange(blob);

      event.target.value = "";
    }
  };

  const handleOpenCamera = async () => {
    setOpen(false);
    setShowCamera(true);
    handleReset();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error("Camera access error:", error);
      fileInputRef.current?.click();
    }
  };

  const handleTakePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const context = canvasRef.current.getContext("2d");
      if (context) {
        canvasRef.current.width = videoRef.current.videoWidth;
        canvasRef.current.height = videoRef.current.videoHeight;
        context.drawImage(videoRef.current, 0, 0);
        canvasRef.current.toBlob((blob) => {
          if (blob) {
            setBlobData(blob);
            setPreview(URL.createObjectURL(blob));
            onChange(blob);
          }
        }, "image/jpeg");
      }
    }
    stopCamera();
  };

  const stopCamera = () => {
    setShowCamera(false);

    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach((track) => {
        track.stop();
      });
      videoRef.current.srcObject = null;
    }
  };

  const handleGallerySelect = () => {
    setOpen(false);
    fileInputRef.current?.click();
  };

  const handleReset = () => {
    setPreview(null);
    setBlobData(null);
    onChange(null);
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      alignItems="center"
      justifyContent="center"
      width="100%"
    >
      {/* Image Preview */}
      {preview && (
        <Box>
          <img
            src={preview}
            alt="Captured"
            width="100%"
            style={{ maxHeight: "200px" }}
          />
        </Box>
      )}

      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        onChange={handleCapture}
        style={{ display: "none" }}
      />

      {showCamera ? null : !blobData ? (
        <Box
          onClick={() => setOpen(true)}
          sx={{
            borderRadius: "12px",
            border: "1px solid rgba(255, 255, 255, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            backgroundColor: "#1A1A1A",
            color: "rgba(255, 255, 255, 0.5)",
            "&:hover": { borderColor: "#fff", color: "#fff" },
          }}
        >
          <PhotoCameraIcon sx={{ fontSize: 48 }} />
        </Box>
      ) : (
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => setOpen(true)}
        >
          ReUpload
        </Button>
      )}

      {preview && (
        <Button variant="outlined" color="secondary" onClick={handleReset}>
          Remove Image
        </Button>
      )}

      {required && !showCamera && !preview && (
        <Typography color="error" variant="caption">
          Image is required
        </Typography>
      )}

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Select Image Source</DialogTitle>
        <DialogActions>
          {camera && (
            <Button startIcon={<PhotoCameraIcon />} onClick={handleOpenCamera}>
              Camera
            </Button>
          )}
          {gallery && (
            <Button
              startIcon={<PhotoLibraryIcon />}
              onClick={handleGallerySelect}
            >
              Gallery
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {showCamera && (
        <Box display="flex" flexDirection="column" alignItems="center">
          <video
            ref={videoRef}
            autoPlay
            style={{ width: "100%", maxHeight: "300px" }}
          />
          <Button variant="contained" color="primary" onClick={handleTakePhoto}>
            Capture Photo
          </Button>
          <Button variant="outlined" color="secondary" onClick={stopCamera}>
            Close Camera
          </Button>
          <canvas ref={canvasRef} style={{ display: "none" }} />
        </Box>
      )}
    </Box>
  );
};

// {
//   id: "submit-button",
//   name: "Button",
//   icon: "🚀",
//   component: (props: any) => (
//     <div style={{ width:"100%", display: "flex", justifyContent: "center" }}>
//       <Button
//         variant="contained"
//         style={{
//           width: props.width,
//           height: props.height,
//           backgroundColor: props.color || "#1976d2",
//           borderRadius: props.borderRadius || "4px",
//         }}
//         onClick={props.onClick}
//       >
//         {props.label}
//       </Button>
//     </div>
//   ),
//   config: {
//     label: "Submit",
//     width: "100%",
//     height: "40px",
//     borderRadius: "8px",
//     color: "#4CAF50",
//     onClick: () => {
//       console.log("Submit button clicked");
//     },
//   },
// },
